import React, { useState, useEffect, useContext } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import classes from './styles.module.css'

import {
    Box,
    Typography,
    Button,
    Divider,
    Grid,
    Paper,
    TextField,
    IconButton,
    InputAdornment,
    Container,
    Tooltip,
} from '@mui/material'
import RemoveIcon from '@mui/icons-material/Remove'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { UserContext } from '../../contexts/UserContext'
import {
    getAddonPrice,
    getBackendUserUrl,
    getCurrentUser,
    getSponsorPrice,
} from '../../utils/utils'
import axios from 'axios'
import ServiceSection from '../../components/bookings/ServiceSection'
import VenueDetailView from '../../components/venue/VenueDetailView'
import ServiceDetailView from '../../components/bookings/ServiceDetailView'

import EventDetailsCard from '../../components/common/EventDetailsCard'

dayjs.extend(utc)
dayjs.extend(timezone)

// TODO: Add to cart backend functionality so on refresh the content is saved. 

const ShoppingCartPage = () => {
    const location = useLocation()
    const { user } = useContext(UserContext)
    const [isLoadingCartData, setIsLoadingCartData] = useState(false)
    // const [isLoadingCartData, setIsLoadingV] = useState(false)

    const [venue, setVenue] = useState(location.state?.venue || null)
    const [venueSpacesCart, setVenueSpacesCart] = useState([])
    const [cartAddons, setCartAddons] = useState([])
    const isTemplate = location.state?.isTemplate || false
    const [error, setError] = useState('')
    const [expandedServiceId, setExpandedServiceId] = useState(null)
    const [selectedService, setSelectedService] = useState(null)
    const [isEditing, setIsEditing] = useState(false)
    const [isReadyToCreateDraft, setIsReadyToCreateDraft] = useState(false)

    // Initialize event details with proper date handling
    const [eventDetails, setEventDetails] = useState(() => {
        if (location.state?.templateData) {
            const t = location.state.templateData;
            return {
                eventName: t.title || '',
                eventVision: t.eventVision || '',
                description: t.description || '',
                eventTypes: Array.isArray(t.eventType) ? t.eventType : [],
                startDate: t.eventStartDate || null,
                endDate: t.eventEndDate || null,
                location: t.venueTitle || location.state.venue?.title || '',
                guestCount: typeof t.guestCount === 'number' ? t.guestCount : 0,
            };
        }
        return {
            eventName: '',
            eventVision: '',
            description: '',
            eventTypes: [],
            startDate: null,
            endDate: null,
            location: location.state?.venue?.title || '',
            guestCount: 0,
        };
    });

    // Update event details when template data changes
    useEffect(() => {
        if (isTemplate && location.state?.venue) {
            setVenue(location.state.venue);
            setVenueSpacesCart(location.state.venue.VenueSpaces || []);
            setCartAddons([
                ...(location.state.cartAddons || []),
                ...(location.state.sponsors || [])
            ]);
            
            if (location.state?.templateData) {
                const t = location.state.templateData;
                setEventDetails(prev => ({
                    ...prev,
                    eventName: t.title || prev.eventName,
                    eventVision: t.eventVision || prev.eventVision,
                    description: t.description || prev.description,
                    eventTypes: Array.isArray(t.eventType) ? t.eventType : prev.eventTypes,
                    startDate: t.eventStartDate || prev.startDate,
                    endDate: t.eventEndDate || prev.endDate,
                    location: t.venueTitle || location.state.venue?.title || prev.location,
                    guestCount: typeof t.guestCount === 'number' ? t.guestCount : prev.guestCount,
                }));
            }
        } else if (!isTemplate) {
            fetchCartData();
        }
    }, [isTemplate, location.state]);

    // Debug: log after state updates
    useEffect(() => {
        if (isTemplate) {
            console.log('DEBUG: venue (after set)', venue);
            console.log('DEBUG: venueSpacesCart (after set)', venueSpacesCart);
            console.log('DEBUG: cartAddons (after set)', cartAddons);
        }
        console.log("DEBUG NO TEMPLATE VENUE", venue);
        console.log("eventDetails", eventDetails);

    }, [venue, venueSpacesCart, cartAddons, isTemplate]);

    // If there are no more venue spaces, delete all the addons.
    useEffect(() => {
        if (!isTemplate && venueSpacesCart.length === 0) {
            setCartAddons([]);
        }
    }, [venueSpacesCart, isTemplate]);

    // Create venue services array - moved outside of render
    const venueServices = React.useMemo(() => {
        if (!venue) return [];
        return [{
            ...venue,
            id: venue.venueId,
            title: venue.title,
            coverPhoto: venue.coverPhoto,
            description: venue.desc || venue.description,
            capacity: venue.capacity,
            features: venue.features,
            company: venue.company
        }];
    }, [venue]); // Only recompute when venue changes

    // Validation uses eventDetails.eventName
    useEffect(() => {
        console.log("Venue spaces cart updated:", venueSpacesCart);
    }, [venueSpacesCart]);

    const validateCheckout = () => {
        console.log("Validating checkout with venueSpacesCart:", venueSpacesCart);
        if (!eventDetails.eventName?.trim()) {
            toast.error('Event details are required.');
            return false;
        }
        if (!eventDetails.startDate || !eventDetails.endDate) {
            toast.error('Please select a start and end date for your event.');
            return false;
        }
        return true;
    };

    // Create Draft uses eventDetails
    const handleCreateDraftButtonClick = async () => {
        if (!validateCheckout()) return
        
        // Ensure all required fields are present and properly formatted
        const eventPayload = {
            title: eventDetails.eventName?.trim(),
            hostId: user.userId,
            venue: venue,
            guestCount: typeof eventDetails.guestCount === 'number' ? eventDetails.guestCount : 0,
            eventVision: eventDetails.eventVision?.trim(),
            venueSpacesIds: venueSpacesCart.map(space => space.venueSpaceId).filter(Boolean),
            eventStartDate: eventDetails.startDate ? dayjs(eventDetails.startDate).toISOString() : null,
            eventEndDate: eventDetails.endDate ? dayjs(eventDetails.endDate).toISOString() : null,
            addons: cartAddons,
        };        
        try {
            const user = await getCurrentUser()
            if (!user) {
                toast.error('Please login.')
                return false
            }
            
            // Create the event
            let response = await fetch(getBackendUserUrl() + '/event/create_event', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(eventPayload),
            })
            let responseData = await response.json()
            if (!response.ok) throw new Error(responseData.detail || 'Failed to create event')
            
            // Get the event ID from the created event
            const eventId = responseData.eventId
            
            // Create assignment group named "Internal"
            const assignmentGroupResponse = await fetch(`${getBackendUserUrl()}/agenda/assignment_groups/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    event_id: eventId,
                    name: 'Internal',
                    created_by: user.userId
                }),
            })
            
            if (!assignmentGroupResponse.ok) {
                console.error('Failed to create assignment group')
            } else {
                const assignmentGroupData = await assignmentGroupResponse.json()
                const assignmentGroupId = assignmentGroupData.assignmentGroupId
                
                // Add current user as member to the assignment group
                const addMemberResponse = await fetch(`${getBackendUserUrl()}/agenda/assignment_group_members/`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        assignment_group_id: assignmentGroupId,
                        user_id: user.userId
                    }),
                })
                
                if (!addMemberResponse.ok) {
                    console.error('Failed to add user to assignment group')
                }
            }
            
            // Clear the cart
            let apiUrl = `${getBackendUserUrl()}/cart/delete_cart_for_user_venue`
            const requestBody = { user_id: user.userId }
            response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody),
            })
            responseData = await response.json()
            if (!response.ok) throw new Error(responseData.detail || 'Failed to create event')
            
            window.location.href = '/myevents'
        } catch (error) {
            toast.error(`Failed to create event: ${error.message}`)
        }
    }
    // Save handler for EventDetailsCard
    const handleSave = (updated) => {
        // Ensure all fields are properly formatted
        const formattedUpdate = {
            eventName: updated.eventName?.trim() || eventDetails.eventName,
            description: updated.description?.trim() || eventDetails.description,
            eventVision: updated.eventVision?.trim() || eventDetails.eventVision,
            eventTypes: Array.isArray(updated.eventTypes) ? updated.eventTypes : eventDetails.eventTypes,
            startDate: updated.startDate || eventDetails.startDate,
            endDate: updated.endDate || eventDetails.endDate,
            location: updated.location?.trim() || eventDetails.location,
            guestCount: typeof updated.guestCount === 'number' ? updated.guestCount : eventDetails.guestCount,
        };
        setEventDetails(formattedUpdate);
        setIsEditing(false);
    };

    const handleImageUpload = (file, imageUrl) => {
        setVenue(prev => ({
            ...prev,
            coverPhoto: imageUrl
        }))
    }

    // Restore cart-related functions
    const fetchCartData = async () => {
        setIsLoadingCartData(true)
        const user = await getCurrentUser()
        if (!user) {
            setIsLoadingCartData(false)
            toast.error('Please log in to view your cart')
            return
        }
        const backendUrl = `${getBackendUserUrl()}/cart/get_cart_for_user_venue`
        const requestBody = { user_id: user.userId }
        try {
            const response = await axios.post(backendUrl, requestBody)
            const venueSpacesInCart = response.data.venue_spaces_ids
            const matchedVenueSpaces = []
            venueSpacesInCart.forEach((spaceIdInCart) => {
                venue.VenueSpaces.forEach((venueSpace) => {
                    if (spaceIdInCart == venueSpace.venueSpaceId) {
                        const exists = matchedVenueSpaces.some(
                            (existingSpace) =>
                                existingSpace.venueSpaceId ===
                                venueSpace.venueSpaceId
                        )
                        if (!exists) {
                            matchedVenueSpaces.push(venueSpace)
                        }
                    }
                })
            })
            setVenueSpacesCart(matchedVenueSpaces)
            const venueAddonsInCart = response.data.venue_add_ons_ids
            const matchedAddonsWithPackages = []
            venueAddonsInCart.forEach((addonInCart) => {
                venue.VenueAddOns.forEach((venueAddOn) => {
                    if (addonInCart.addonId === venueAddOn.addonId) {
                        const updatedAddon = {
                            ...venueAddOn,
                            packageId: addonInCart.packageId,
                        }
                        const exists = matchedAddonsWithPackages.some(
                            (existingAddon) =>
                                existingAddon.addonId === updatedAddon.addonId
                        )
                        if (!exists) {
                            matchedAddonsWithPackages.push(updatedAddon)
                        }
                    }
                })
            })
            venueAddonsInCart.forEach((sponsorInCart) => {
                venue.VenueSponsors.forEach((venueSponsor) => {
                    if (sponsorInCart.addonId === venueSponsor.id) {
                        const updatedAddon = {
                            ...venueSponsor,
                            packageId: sponsorInCart.packageId,
                        }
                        const exists = matchedAddonsWithPackages.some(
                            (existingAddon) =>
                                existingAddon.addonId === updatedAddon.id
                        )
                        if (!exists) {
                            matchedAddonsWithPackages.push(updatedAddon)
                        }
                    }
                })
            })
            setCartAddons(matchedAddonsWithPackages)
            console.log("cartAddons", cartAddons);
        } catch (err) {
            console.log('This user has no items in their cart yet.')
        } finally {
            setIsLoadingCartData(false)
        }
    }
    const handleRemoveVenueSpace = (venueSpace) => {
        setVenueSpacesCart(prevSpaces =>
            prevSpaces.filter(space => space.venueSpaceId !== venueSpace.venueSpaceId)
        );
    }
    const handleRemoveSponsor = async (sponsor) => {
        //cartAddons service 
        console.log("HandleRemoveSponsor sponsor", sponsor);
        setCartAddons((prevAddons) =>
            prevAddons.filter(
                (addon) => (addon.id !== sponsor.id && addon.addonId !== sponsor.addonId)
            )
        );

        // const user = await getCurrentUser()
        // if (!user) {
        //     toast.error('Please login to remove items from your cart')
        //     return false
        // }
        // const backendUserUrl = `${getBackendUserUrl()}/cart/remove_sponsor_from_cart`
        // const requestBody = {
        //     user_id: user.userId,
        //     venue_id: venue.venueId,
        //     sponsor_id: sponsor.id,
        //     package_id: sponsor.packageId,
        // }
        // try {
        //     const response = await axios.post(backendUserUrl, requestBody)
        //     setCartAddons((prevAddons) =>
        //         prevAddons.filter(
        //             (a) => a.id === undefined || a.id !== sponsor.id
        //         )
        //     )
        //     toast.success(response.data.message)
        //     return true
        // } catch (err) {
        //     if (err.response && err.response.data) {
        //         const errorDetail = err.response.data.detail
        //         switch (errorDetail) {
        //             case 'UserNotFound':
        //                 toast.error(
        //                     'User not found. Please make sure you are logged in.'
        //                 )
        //                 break
        //             case 'CartNotFound':
        //                 toast.error('Your cart is empty.')
        //                 break
        //             case 'SponsorNotFoundInCart':
        //                 toast.error('This sponsor is not in your cart.')
        //                 break
        //             default:
        //                 toast.error(
        //                     'Something went wrong. Please try again later.'
        //                 )
        //                 break
        //         }
        //     } else {
        //         toast.error(
        //             'An unexpected error occurred. Please try again later.'
        //         )
        //     }
        //     return false
        // }
    }
    const handleRemoveAddon = async (addon) => {
        console.log("HandleRemoveAddon addon", addon);
        console.log("prevAddons", cartAddons);
        setCartAddons((prevAddons) =>
            prevAddons.filter(
                (item) => item.companyId !== addon.companyId
                // (item) => (item.id !== addon.id && item.addonId !== addon.addonId)
            )
        );

        // const user = await getCurrentUser()
        // if (!user) {
        //     toast.error('Please login to remove items from your cart')
        //     return false
        // }
        // const backendUserUrl = `${getBackendUserUrl()}/cart/remove_addon_from_cart`
        // const requestBody = {
        //     user_id: user.userId,
        //     venue_add_on_id: {
        //         addon_id: addon.addonId,
        //         package_id: addon.packageId,
        //     },
        // }
        // try {
        //     const response = await axios.post(backendUserUrl, requestBody)
        //     setCartAddons((prevAddons) =>
        //         prevAddons.filter((a) => a.addonId !== addon.addonId)
        //     )
        //     toast.success(response.data.message)
        //     return true
        // } catch (err) {
        //     if (err.response && err.response.data) {
        //         const errorDetail = err.response.data.detail
        //         switch (errorDetail) {
        //             case 'UserNotFound':
        //                 toast.error(
        //                     'User not found. Please make sure you are logged in.'
        //                 )
        //                 break
        //             case 'CartNotFound':
        //                 toast.error('Your cart is empty.')
        //                 break
        //             case 'AddOnNotFoundInCart':
        //                 toast.error('This add-on is not in your cart.')
        //                 break
        //             default:
        //                 toast.error(
        //                     'Something went wrong. Please try again later.'
        //                 )
        //                 break
        //         }
        //     } else {
        //         toast.error(
        //             'An unexpected error occurred. Please try again later.'
        //         )
        //     }
        //     return false
        // }
    }
    const calculateTotal = () => {
        const initialValue = 0;
        const venueSpacesPriceTotal = venueSpacesCart.reduce(
            (accumulator, currentVenueSpace) => {
                return accumulator + Number(currentVenueSpace.startingPrice);
            },
            initialValue
        );
        // Only sum the price of selected packages for each addon
        const addonsOtherThanSponsors = cartAddons.filter(
            (addon) => addon.addonType !== 'General Sponsor' && addon.selectedPackage
        );
        const sponsorAddons = cartAddons.filter(
            (addon) => addon.addonType === 'General Sponsor' && addon.selectedPackage
        );
        const otherAddonPrices = addonsOtherThanSponsors.reduce(
            (accumulator, addon) => {
                let pkg = addon.packages && addon.selectedPackage
                    ? (addon.packages.find(p => (p.id || p.addonId) === addon.selectedPackage) || addon.selectedPackage)
                    : addon.selectedPackage;
                return accumulator + (pkg && pkg.price ? Number(pkg.price) : 0);
            },
            initialValue
        );
        const sponsorPrices = sponsorAddons.reduce(
            (accumulator, addon) => {
                let pkg = addon.packages && addon.selectedPackage
                    ? (addon.packages.find(p => (p.id || p.addonId) === addon.selectedPackage) || addon.selectedPackage)
                    : addon.selectedPackage;
                return accumulator + (pkg && pkg.price ? Number(pkg.price) : 0);
            },
            initialValue
        );
        return venueSpacesPriceTotal + otherAddonPrices - sponsorPrices;
    }
    // const handleManageClick = (service) => {
    //     console.log("handleManageClick service", service);
    //     const serviceId = service.addonId || service.id || service.venueSpaceId;
    //     const companyId = service.companyId;
    //     const selectedId = selectedService?.addonId || selectedService?.id || selectedService?.venueSpaceId;
    //     // setSelectedService(companyId === selectedId ? null : service);
    //     setSelectedService(service);
    // }
    const handleManageClick = (service) => {
        console.log("handleManageClick service", service);
        if (!service) {
            setExpandedServiceId(null);
            return;
        }
        // Use venueId for venues, and companyId for other services.
        const id = service?.venueId || service.companyId;

        // If clicking the same service that's already expanded, collapse it
        if (expandedServiceId === id) {
            setExpandedServiceId(null);
        } else {
            setExpandedServiceId(id);
        }
    };

    const handleMessageClick = (service) => {
        console.log('Message clicked for:', service)
    }
    const handleRemoveClick = (service) => {
        if (service.venueSpaceId) {
            handleRemoveVenueSpace(service)
        } else if (service.addonType === 'general sponsor') {
            handleRemoveSponsor(service)
        } else {
            handleRemoveAddon(service)
        }
    }

    const handleRequestPackage = (pkg) => {
        // Add the selected package as an addon (or sponsor if type includes sponsor)
        const newAddon = {
            ...pkg,
            companyId: pkg.companyId || pkg.company_id || pkg.company_id,
            addonId: pkg.addonId || pkg.id,
            packageId: pkg.packageId,
            companyName: pkg.companyName || pkg.name,
            addonType: pkg.type || 'service',
            status: 'pending',
            selected: true,
            selectedPackage: pkg.packageId || pkg.id
        };
        
        // Add to cartAddons instead of eventDetails
        setCartAddons(prev => [...prev, newAddon]);
    };

    const handleRemoveService = async (service) => {
        try {
            const companyId = service.companyId || service.id;
            
            // const response = await axios.delete(`${getBackendUserUrl()}/event/remove_vendor_for_event/${eventData.eventId}/${companyId}`);
            // console.log("Vendor added successfully:", response.data);

            // Remove from cartAddons instead of eventDetails
            setCartAddons(prev => 
                prev.filter(addon => addon.companyId !== companyId)
            );
            setExpandedServiceId(null);
        } catch (error) {
            console.error("Error removing service:", error);
            if (error.response) {
                console.error("API Error:", error.response.data);
            }
        }
    };

    // const renderAddonContent = (addon) => (
    //     <Box sx={{ p: 2 }}>
    //         <Typography variant="h6" gutterBottom>Addon Details</Typography>
    //         <Box display="flex" justifyContent="space-between" mb={1}>
    //             <Typography variant="body2">Type</Typography>
    //             <Typography variant="body2">{addon.addonType}</Typography>
    //         </Box>
    //         <Box display="flex" justifyContent="space-between" mb={1}>
    //             <Typography variant="body2">Price</Typography>
    //             <Typography variant="body2" style={{ color: addon.addonType === 'General Sponsor' ? 'green' : 'inherit' }}>
    //                 {addon.price === null ? (
    //                     <span style={{ color: 'green' }}>PENDING</span>
    //                 ) : (
    //                     `$ ${getAddonPrice(addon)}`
    //                 )}
    //             </Typography>
    //         </Box>
    //     </Box>
    // )

    // Filter addons by type and normalize the data structure
    const filterAddonsByType = (addons, type) => {
        console.log("Filtering addons by type:", type, "from:", addons);
        return addons
            .filter(addon => {
                const addonType = addon.addonType?.toLowerCase();
                const targetType = type.toLowerCase();
                // Handle special case for sponsors
                if (targetType === 'general sponsor') {
                    return addonType === 'general sponsor';
                }
                return addonType === targetType;
            })
            .map(addon => ({
                id: addon.addonId || addon.id,
                name: addon.companyName || addon.name,
                logo: addon.logo || addon.logoUrl,
                status: addon.status || addon.requestStatus,
                price: addon.price,
                description: addon.desc || addon.description,
                packages: addon.packages || [],
                selectedPackage: addon.selectedPackage,
                addonType: addon.addonType,
                // Preserve any other relevant fields
                ...addon
            }));
    };
    
    // Add this function to handle new service additions
    const handleServiceAdded = (newService) => {
        console.log("New service being added:", newService);
        
        // Structure the service data consistently
        const serviceData = {
            ...newService,
            addonId: newService.addonId || newService.id,
            companyName: newService.companyName || newService.name,
            addonType: newService.addonType?.toLowerCase(),
            status: 'pending',
            ...(newService.packages && { packages: newService.packages }),
            ...(newService.selectedPackage && { selectedPackage: newService.selectedPackage })
        };

        console.log("Structured service data:", serviceData);

        // Simply append the new service to cartAddons
        setCartAddons(prev => [...prev, serviceData]);
    };

    const handleVenueAdded = (newVenue) => {
        console.log("New venue being added:", newVenue);
        
        // Structure the venue data consistently
        const venueData = {
            ...newVenue,
            ...newVenue?.Venue,  // Spread the Venue properties first
            company: newVenue?.Company,  // Add company information
            // Initialize arrays
            VenueSpaces: newVenue.Venue?.VenueSpaces || [],
            VenueAddOns: newVenue.Venue?.VenueAddOns || [],
            VenueSponsors: newVenue.Venue?.VenueSponsors || []
        };
        
        console.log("Structured venue data:", venueData);
        setVenue(venueData);
        setIsReadyToCreateDraft(true);
        
        // If the venue has spaces, add them to the cart
        if (venueData?.VenueSpaces && Array.isArray(venueData?.VenueSpaces)) {
            setVenueSpacesCart(venueData.VenueSpaces);
        }
    };

    const renderExpandedContent = (service) => {
        console.log("Render expanded content service", service);
        if (service.venueId) {
            return (
                <VenueDetailView
                    venue={service}
                    event={eventDetails} 
                    // venueRequest={venueRequest}
                    // setVenueRequest={setVenueRequest}
                    onMinimize={() => handleManageClick(null)}
                />
            );
        }
        // For catering, sponsors, other services
        return (
            <ServiceDetailView
                service={service}
                // selectedPackages={getSelectedPackagesForCompany(service.companyId || service.id)}
                onRequestPackage={handleRequestPackage}
                // onRequestCustomPackage={handleRequestCustomPackage}
                onRemove={handleRemoveService}
                // customRequestStatus={customRequestStatus[service.companyId || service.id]}
                onMinimize={() => handleManageClick(null)}
            />
        );
    };


    // Filter different types of services
    const catering = filterAddonsByType(cartAddons, 'catering');
    const sponsors = filterAddonsByType(cartAddons, 'general sponsor');
    const otherServices = filterAddonsByType(cartAddons, 'other service provider');

    console.log("Venue services being passed:", venueServices);
    console.log("venue", venue);
    console.log("venueSpacesCart", venueSpacesCart);
    console.log("cartAddons", cartAddons);
    console.log("catering", catering);
    console.log("sponsors", sponsors);
    console.log("otherServices", otherServices);

    // Debug logging
    useEffect(() => {
        console.log("Venue state updated:", venue);
        console.log("Venue services updated:", venueServices);
    }, [venue, venueServices]);

    // In ShoppingCart.js
    useEffect(() => {
        console.log('ShoppingCart - Current cartAddons:', cartAddons);
        console.log('ShoppingCart - Filtered services:', {
            catering,
            sponsors,
            otherServices
        });
    }, [cartAddons, catering, sponsors, otherServices]);

    if (error) return <div>Error: {error}</div>
    return (
        <div className="container mt-4">
            <Box>
                <Typography variant="h4" gutterBottom>
                    Request to book
                </Typography>
                <Grid container spacing={4} alignItems="stretch" sx={{ minHeight: { md: 320, xs: 'auto' }, height: { md: 520, xs: 'auto' } }}>
                    <Grid item xs={12} md={9} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Your Event Details
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <EventDetailsCard
                                eventDetails={eventDetails}
                                isEditing={isEditing}
                                onEdit={() => setIsEditing(true)}
                                onCancel={() => setIsEditing(false)}
                                onSave={handleSave}
                                onCreateDraft={handleCreateDraftButtonClick}
                                imageUrl={venue?.coverPhoto}
                                onImageUpload={handleImageUpload}
                                sx={{ height: '100%' }}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Paper elevation={2} sx={{
                            p: 3,
                            borderRadius: 3,
                            boxShadow: 2,
                            bgcolor: 'background.paper',
                            mb: 4,
                            flex: 1,
                            height: '100%',
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                            <Typography variant="h6" gutterBottom>
                                Cart Summary
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            {venue && (
                                <Box display="flex" alignItems="center" mb={2}>
                                    <img
                                        src={venue.coverPhoto}
                                        alt={venue.title}
                                        style={{
                                            borderRadius: '8px',
                                            marginRight: '16px',
                                            width: '120px',
                                            height: '80px',
                                            objectFit: 'cover',
                                        }}
                                    />
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {venue.title}
                                        </Typography>
                                        <Box display="flex" justifyContent="space-between" mb={1}>
                                            <Typography variant="body2" color="text.secondary">Total Price</Typography>
                                            <Typography variant="body2" color="primary.main">${calculateTotal()}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1, mt: 2 }}>Venue Spaces</Typography>
                            {isLoadingCartData ? (
                                <Box display="flex" justifyContent="center" alignItems="center" height="100px">
                                    <div>Loading cart data...</div>
                                </Box>
                            ) : (
                                <>
                                    {venueSpacesCart.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">No venue spaces in your cart.</Typography>
                                    ) : (
                                        venueSpacesCart.map((venueSpace, index) => (
                                            <Box key={index} mb={2}>
                                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{venueSpace.title}</Typography>
                                                    <IconButton size="small" color="error" onClick={() => handleRemoveVenueSpace(venueSpace)}>
                                                        <RemoveIcon />
                                                    </IconButton>
                                                </Box>
                                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                                    <Typography variant="body2" color="text.secondary">Price</Typography>
                                                    <Typography variant="body2" color={venueSpace.startingPrice === null ? 'success.main' : 'text.primary'}>
                                                        {venueSpace.startingPrice === null ? (
                                                            <span style={{ color: 'green' }}>PENDING</span>
                                                        ) : (
                                                            `$ ${venueSpace.startingPrice}`
                                                        )}
                                                    </Typography>
                                                </Box>
                                                {index < venueSpacesCart.length - 1 && <Divider sx={{ my: 1 }} />}
                                            </Box>
                                        ))
                                    )}
                                    <Divider sx={{ my: 2, borderColor: 'primary.main', borderBottomWidth: 2 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1, mt: 2 }}>Addons</Typography>
                                    {cartAddons.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">No addons in your cart.</Typography>
                                    ) : (
                                        cartAddons.map((item, index) => {
                                            const isSponsor = item.addonType && item.addonType.toLowerCase().includes('sponsor');
                                            const pkg = item.packages && item.selectedPackage
                                                ? (item.packages.find(p => (p.id || p.addonId) === item.selectedPackage) || item.selectedPackage)
                                                : item.selectedPackage;
                                            return (
                                                <Box key={index} mb={2}>
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                        <Box>
                                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>{item.companyName}</Typography>
                                                            <Typography variant="body2" color="text.secondary">{item.addonType}</Typography>
                                                            {pkg ? (
                                                                <>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                                                                        {pkg.name || pkg.title || `Package: ${typeof pkg === 'string' ? pkg : ''}`}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="primary.main">
                                                                        {pkg.price ? `$${pkg.price} minimum spend` : ''}
                                                                    </Typography>
                                                                    {pkg.description && (
                                                                        <Typography variant="body2" color="text.secondary">{pkg.description}</Typography>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <Typography variant="body2" color="warning.main">No package selected</Typography>
                                                            )}
                                                        </Box>
                                                        <IconButton size="small" color="error" onClick={() => {
                                                            if (item.addonType === 'General Sponsor') {
                                                                handleRemoveSponsor(item)
                                                            } else {
                                                                handleRemoveAddon(item)
                                                            }
                                                        }}>
                                                            <RemoveIcon />
                                                        </IconButton>
                                                    </Box>
                                                    {index < cartAddons.length - 1 && <Divider sx={{ my: 1 }} />}
                                                </Box>
                                            )
                                        })
                                    )}
                                </>
                            )}
                            <Divider sx={{ my: 2 }} />
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="body1" fontWeight="bold">Total (USD)</Typography>
                                <Typography variant="body1" fontWeight="bold">${calculateTotal()}</Typography>
                            </Box>
                        </Paper>
                        <Box mt={4}>
                            <Tooltip 
                                title={isEditing ? "Please finish editing the event details before creating draft" : "You must add a venue"}
                                open={isEditing || !isReadyToCreateDraft}
                                placement="top"
                            >
                                <span style={{ width: '100%' }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        onClick={handleCreateDraftButtonClick}
                                        fullWidth
                                        disabled={isEditing || !isReadyToCreateDraft}
                                        sx={{ mt: 2, py: 1.5, fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' }, textAlign: 'center', minHeight: 56, maxWidth: '100%', overflow: 'hidden' }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'block',
                                                width: '100%',
                                                maxWidth: '100%',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' },
                                            }}
                                        >
                                            {eventDetails.eventName?.trim() ? `Create Draft: ${eventDetails.eventName}` : 'Create Draft'}
                                        </Box>
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>
                    </Grid>
                </Grid>
                {/* ServiceSection components below summary */}
                <Box mt={6}>
                    <ServiceSection
                        key={venue?.venueId || 'no-venue'} // Add key to force re-render when venue changes
                        sectionTitle="Venue"
                        services={venueServices}
                        onManageClick={handleManageClick}
                        onMessageClick={handleMessageClick}
                        onRemoveClick={handleRemoveClick}
                        expandedServiceId={expandedServiceId}
                        // expandedService={selectedService}
                        onVenueAdded={handleVenueAdded}
                        renderExpandedContent={renderExpandedContent}
                    />
                    <ServiceSection
                        sectionTitle="Catering"
                        services={catering}
                        onManageClick={handleManageClick}
                        onMessageClick={handleMessageClick}
                        onRemoveClick={handleRemoveClick}
                        expandedServiceId={expandedServiceId}
                        // expandedService={selectedService}
                        onServiceSelected={handleServiceAdded}
                        renderExpandedContent={renderExpandedContent}
                    />
                    <ServiceSection
                        sectionTitle="Sponsors"
                        services={sponsors}
                        onManageClick={handleManageClick}
                        onMessageClick={handleMessageClick}
                        onRemoveClick={handleRemoveClick}
                        expandedServiceId={expandedServiceId}
                        // expandedService={selectedService}
                        onServiceSelected={handleServiceAdded}
                        renderExpandedContent={renderExpandedContent}
                    />
                    <ServiceSection
                        sectionTitle="Other Services"
                        services={otherServices}
                        onManageClick={handleManageClick}
                        onMessageClick={handleMessageClick}
                        onRemoveClick={handleRemoveClick}
                        expandedServiceId={expandedServiceId}
                        // expandedService={selectedService}
                        onServiceSelected={handleServiceAdded}
                        renderExpandedContent={renderExpandedContent}
                    />
                </Box>
            </Box>
            <ToastContainer />
        </div>
    )
}

export default ShoppingCartPage
